import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { usePurchaseVendorSelector } from 'src/components/Selectors/PurchaseVendorSelector/usePurchaseVendorSelector';
import { useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { DivWithTitle } from 'src/core/ui/DivWithTitle';
import { useUserGroups } from 'src/hooks/useUserGroups';
import { ContentId } from 'src/utils/constants/contentId';
import {
  PurchaseVendor,
  SellerUserAccountInfo,
  Tag,
  TagsValueType,
  UserGroup,
} from 'src/WebApiController';

import * as styles from '../TagsFormBody.css';

export const TagValueDisplay = ({
  tag,
  displayMode,
}: {
  tag: Tag;
  displayMode?: boolean;
}) => {
  if (tag.valueType === TagsValueType.Url && displayMode) {
    return <UrlDisplay tag={tag} />;
  }

  if (tag.valueType === TagsValueType.Date) {
    return <DateDisplay tag={tag} />;
  }

  if (tag.valueType === TagsValueType.POSUser) {
    return <POSUserDisplay tag={tag} />;
  }

  if (tag.valueType === TagsValueType.UserGroup) {
    return <UserGroupDisplay tag={tag} />;
  }

  if (tag.valueType === TagsValueType.Vendor) {
    return <VendorDisplay tag={tag} />;
  }

  return (
    <DivWithTitle className={styles.textSecondary}>{tag.value}</DivWithTitle>
  );
};

export const resolveTagValueDisplay = (
  tag: Tag,
  allActiveUserInfos: Record<string, SellerUserAccountInfo> | null,
  userGroups: UserGroup[] | null | undefined,
  availableVendors: Record<string, PurchaseVendor>,
  unknownText: string,
  timeZone: string
) => {
  if (tag.valueType === TagsValueType.POSUser) {
    const sellerUserId = tag.value;

    const user =
      !sellerUserId || !allActiveUserInfos
        ? null
        : allActiveUserInfos[sellerUserId];
    return user?.displayName || unknownText;
  }

  if (tag.valueType === TagsValueType.UserGroup) {
    const userGroupId = tag.value;
    const userGroup = userGroups?.find(
      (ug) => ug.userGroupId.toString() === userGroupId
    );
    return userGroup?.userGroupName || unknownText;
  }

  if (tag.valueType === TagsValueType.Vendor) {
    const vendorId = tag.value;
    const vendor = availableVendors[vendorId];
    return vendor?.name || unknownText;
  }

  if (tag.valueType === TagsValueType.Date) {
    const date = new Date(tag.value);
    return formatInTimeZone(date, timeZone, "MMMM d, yyyy 'at' h:mm a");
  }

  return tag.value;
};

const POSUserDisplay = ({ tag }: { tag: Tag }) => {
  const { allActiveUserInfos } = useSellerAccountContext();

  const unknownText = useContent(ContentId.Unknown);

  const sellerUserId = tag.value;

  const user = useMemo(() => {
    if (!sellerUserId || !allActiveUserInfos) {
      return null;
    }

    return allActiveUserInfos[sellerUserId];
  }, [allActiveUserInfos, sellerUserId]);

  return (
    <div className={styles.textSecondary}>
      {user?.displayName ?? `<${unknownText}>`}
    </div>
  );
};

const UserGroupDisplay = ({ tag }: { tag: Tag }) => {
  const { userGroups } = useUserGroups();

  const unknownText = useContent(ContentId.Unknown);

  const userGroupId = tag.value;

  const userGroup = useMemo(() => {
    if (!userGroupId?.length || !userGroups?.length) {
      return null;
    }

    return userGroups.find((ug) => ug.userGroupId.toString() === userGroupId);
  }, [userGroupId, userGroups]);

  return (
    <div className={styles.textSecondary}>
      {userGroup?.userGroupName ?? `<${unknownText}>`}
    </div>
  );
};

const VendorDisplay = ({ tag }: { tag: Tag }) => {
  const vendorId = tag.value;

  const unknownText = useContent(ContentId.Unknown);

  const { availableVendors } = usePurchaseVendorSelector({});
  const vendor = useMemo(() => {
    if (vendorId == null) {
      return null;
    }

    return availableVendors[vendorId];
  }, [availableVendors, vendorId]);

  return (
    <div className={styles.textSecondary}>
      {vendor?.name ?? `<${unknownText}>`}
    </div>
  );
};

const DateDisplay = ({ tag }: { tag: Tag }) => {
  const { timeZone } = useSiteTimezoneContext();
  return (
    <div className={styles.textSecondary}>
      {tag?.value
        ? formatInTimeZone(
            new Date(tag.value),
            timeZone,
            "MMMM d, yyyy 'at' h:mm a"
          )
        : ''}
    </div>
  );
};

const UrlDisplay = ({ tag }: { tag: Tag }) => {
  const absoluteUrl = useMemo(() => {
    if (tag.valueType !== TagsValueType.Url || !tag.value) {
      return;
    }
    if (
      tag.value.toLocaleLowerCase().startsWith('http') ||
      tag.value.toLocaleLowerCase().startsWith('https')
    ) {
      return tag.value;
    }

    // Browser will automatically use https if the site supports it
    return `http://${tag.value}`;
  }, [tag.value, tag.valueType]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      title={tag.value}
    >
      <a
        className={styles.textLink}
        href={absoluteUrl}
        // So that the link opens in a new tab
        target="_blank"
        rel="noreferrer"
      >
        {tag.value}
      </a>
    </div>
  );
};
